// Cities master client.
//
// Replaces the hardcoded lists that used to live in
//   src/assets/json/cities.json
//   src/assets/json/doctor/doctorarea.json
//   indexbanner.vue defaultcitySuggestion
//
// Everything here is plain data + pure helpers so it can be reused by the
// search box, the doctor forms, the patient profile and the admin screen.
import axios from "axios";

// Sentinel value for the "Other / Enter manually" dropdown entry. Kept out of
// the numeric city_id space so it can never collide with a real city.
export const OTHER_CITY_VALUE = "__other__";
export const OTHER_CITY_LABEL = "Other / Enter manually";

export const CITY_NAME_MAX_LENGTH = 100;

// Pseudo-entry for the home/search city box meaning "do not filter by city".
export const ALL_CITIES_LABEL = "All Cities";
export const ALL_CITIES_OPTION = { city_id: 0, name: ALL_CITIES_LABEL };

/** True when the value is the "All Cities" sentinel rather than a real city. */
export function isAllCities(name) {
  return normalizeCityName(name) === normalizeCityName(ALL_CITIES_LABEL);
}

/**
 * Fold a city name to its duplicate-detection form.
 * Mirrors the backend's normalize() so "Mangalore", " mangalore " and
 * "MANGALORE" all compare equal on both sides.
 */
export function normalizeCityName(name) {
  if (name === null || name === undefined) return "";
  return String(name).trim().replace(/\s+/g, " ").toLowerCase();
}

/** Trim and collapse inner whitespace, preserving the user's capitalisation. */
export function cleanCityName(name) {
  if (name === null || name === undefined) return "";
  return String(name).trim().replace(/\s+/g, " ");
}

/**
 * Validate a manually entered city name.
 * Returns an error message, or "" when the value is acceptable.
 */
export function validateManualCityName(name) {
  const cleaned = cleanCityName(name);
  if (!cleaned) return "Please enter a city name.";
  if (cleaned.length > CITY_NAME_MAX_LENGTH) {
    return `City name must not exceed ${CITY_NAME_MAX_LENGTH} characters.`;
  }
  if (!/^[\p{L}\p{M}][\p{L}\p{M}\s.'-]*$/u.test(cleaned)) {
    return "City name may only contain letters, spaces, apostrophes, hyphens and periods.";
  }
  return "";
}

/** Find a loaded city whose name matches `name`, ignoring case and whitespace. */
export function findCityByName(cities, name) {
  const target = normalizeCityName(name);
  if (!target) return null;
  return (cities || []).find(city => normalizeCityName(city.name) === target) || null;
}

/** GET /cities - active cities only, ordered by name. */
export async function fetchCities() {
  const response = await axios.get("/cities");
  return response.data || [];
}

/**
 * POST /cities - resolve a manually entered city.
 * The backend returns the existing record when one matches, so this never
 * creates a duplicate of an existing city.
 */
export async function resolveCity({ name, state = null, country = "India" }) {
  const response = await axios.post("/cities", {
    name: cleanCityName(name),
    state,
    country
  });
  return response.data;
}
